import { NgIf } from '@angular/common';
import {
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthenticateGuestInput, GuestUser, GuestUserAuthResponse, IAuthResponse } from 'app/shared/model/security.model';
import { MatDividerModule } from '@angular/material/divider';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { FormsModule, NgForm, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

export enum AuthPageType {
    SignIn = 'SignIn',
    ForgotPassword = 'forgotPassword',
}

export type CloseModalResponse = {
    didCreateUser: boolean | undefined;
}

@Component({
    selector: 'guest-auth-form',
    templateUrl: './guest-auth-form.component.html',
    styleUrls: ['./guest-auth-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    imports: [
        NgIf,
        FuseAlertComponent,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatDividerModule,
        MatProgressSpinner,
    ],
})
export class GuestAuthComponent implements OnInit, OnDestroy {
    @ViewChild('guestEmailNgForm') guestEmailNgForm: NgForm;
    @ViewChild('guestPasscodeNgForm') guestPasscodeNgForm: NgForm;
    @Output() successEvent: EventEmitter<GuestUserAuthResponse> = new EventEmitter<GuestUserAuthResponse>();
    @Output() errorEvent: EventEmitter<any> = new EventEmitter<any>();

    alert: { type: FuseAlertType; message: string } = {
        type   : 'success',
        message: '',
    };
    guestEmailForm: UntypedFormGroup;
    guestPasscodeForm: UntypedFormGroup;
    guestAccess: string;
    verifyPasscodeStep: boolean = false;
    INVALID_PASSCODE_MESSAGE = 'Please enter a valid code';
    INVALID_PASSCODE_MESSAGE_RESET = 'Invalid passcode. Please enter your email to request new passcode.';

    private _errorAttemptsRemaining: number = 2;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _authService: AuthService,
        private _formBuilder: UntypedFormBuilder,
    ) {
    }

    ngOnInit(): void {
        this.guestAccess = this._activatedRoute.snapshot.queryParams['guest'];
        this.guestEmailForm = this._formBuilder.group({
            email : ['', [Validators.required, Validators.email]]
        });

        this.guestPasscodeForm = this._formBuilder.group({
            passcode : ['', [Validators.required]]
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    authenticateGuest() : void {
        if (!this.preProcessActionCheck(this.guestEmailForm)) {
            return;
        }

        const input = new AuthenticateGuestInput({
            accessGuid: this.guestAccess,
            email: this.guestEmailForm.value.email,
        });
        this._authService.authenticateGuest(input)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res: GuestUserAuthResponse) => {
                    this.guestEmailForm.enable();
                    this.guestEmailNgForm.resetForm();
                    if (res.success && res.twoFactorAuthenticationRequired) {
                        this.verifyPasscodeStep = true;
                        this.alert = {
                            type   : 'success',
                            message: 'A passcode will be sent to your email address'
                        };
                    } else {
                        this.alert = {
                            type   : 'error',
                            message: 'something went wrong, please try again'
                        };
                    }
                },
                error: (error) => {
                    this.guestEmailForm.enable();
                    this.guestEmailNgForm.resetForm();
                    let msg = error?.result?.message || error?.message || 'something went wrong, please try again';
                    this.alert = {
                        type   : 'error',
                        message: msg
                    };
                    this.checkSessionExpired(error?.result);
                }
            });
    }

    verifyGuestPasscode(): void {
        if (!this.preProcessActionCheck(this.guestPasscodeForm)) {
            return;
        }

        const passcode = this.guestPasscodeForm.value.passcode;
        if (!this.validatePasscode(passcode)) {
            this.failedVerifyPasscode();
            return;
        }

        this._authService.verifyGuestPasscode(passcode)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res: GuestUserAuthResponse) => {
                    if (res.success && !!res.user) {
                        this.successEvent.emit(res);
                        return;
                    } else {
                        this.failedVerifyPasscode();
                    }

                    this.guestPasscodeForm.enable();
                    this.guestPasscodeNgForm.resetForm();
                },
                error: (error) => {
                    this.failedVerifyPasscode();
                    this.checkSessionExpired(error?.result);
                }
            });
    }

    preProcessActionCheck(formGroup: UntypedFormGroup): boolean {
        if (!this.guestAccess) {
            return false;
        }
        if (formGroup.invalid) {
            return false;
        }
        // Disable the form
        formGroup.disable();
        // Hide the alert
        this.alert.message = '';
        this.alert.type = 'success';
        return true;
    }

    private validatePasscode(code: string): boolean {
        const reg = /^\d{8}$/i;
        return !!code && reg.test(code);
    }

    private failedVerifyPasscode() {
        this.guestPasscodeForm.enable();
        this.guestPasscodeNgForm.resetForm();

        this._errorAttemptsRemaining --;
        if(!this._errorAttemptsRemaining) {
            this.verifyPasscodeStep = false;
            this.alert = {
                type: 'error',
                message: this.INVALID_PASSCODE_MESSAGE_RESET,
            };
            return;
        }

        this.alert = {
            type: 'error',
            message:  this.INVALID_PASSCODE_MESSAGE,
        };
    }

    private checkSessionExpired(result: any) {
        if (result?.sessionExpired) {
            this._authService.signOut();
            this.errorEvent.emit();
        }
    }
}
